import { useCallback, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import { GetAccessesForShareDocument, GetAccessesForShareQuery } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useShareTaskProvider } from 'modules/tasks/components/TaskDetails/Header/Share/Provider'
import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'

import { Employee } from './Employee'

interface AccessData {
  id: string
  fullName: string
  firstName: string
  lastName: string
  avatar?: string | null
  active: boolean
  jobTitle?: string | null
}

interface Props {
  task: TaskDetail
}

export const EmployeeList = ({ task }: Props) => {
  const [collapsed, setCollapsed] = useState(false)
  const {
    addedSharedAccesses,
    currentSharedAccesses,
    accessesToRemove,
    newSharedAccessesFiltered,
  } = useShareTaskProvider()

  const [fetchAccesses] = useLazyQuery(GetAccessesForShareDocument)
  const [accessesMap, setAccessesMap] = useState<Map<string, AccessData>>(new Map())

  const newAccessIds = newSharedAccessesFiltered()
  const newAccessIdsKey = newAccessIds.join(',')

  const parseAccessData = useCallback((data: GetAccessesForShareQuery): Map<string, AccessData> => {
    const map = new Map<string, AccessData>()
    for (const access of data.apiCore.accesses) {
      map.set(access.id, {
        id: access.id,
        fullName: access.fullName,
        firstName: access.firstName,
        lastName: access.lastName,
        avatar: access.avatar,
        active: access.employee?.active !== false,
        jobTitle: access.employee?.contractversionsConnection.edges[0]?.node.jobTitle ?? null,
      })
    }
    return map
  }, [])

  useEffect(() => {
    if (newAccessIds.length === 0) {
      setAccessesMap(new Map())
      return
    }

    fetchAccesses({ variables: { ids: newAccessIds } }).then((result) => {
      if (result.data) {
        setAccessesMap(parseAccessData(result.data))
      }
    })
  }, [newAccessIdsKey])

  const assignees = task.assignees.filter((item) => item.id !== task?.author?.id)

  const newAccesses = newAccessIds
    .map((id) => accessesMap.get(id))
    .filter((access): access is AccessData => access != null && access.active)

  return (
    <>
      <Box
        onClick={() => setCollapsed(!collapsed)}
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
      >
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t('tasks.share.modal.employees.title')}
        </Text.Normal>
        <Icon.Medium icon={collapsed ? 'chevron-up' : 'chevron-down'} color="grey1000" />
      </Box>

      {!collapsed && (
        <Box gap="s24">
          <Employee
            disabled
            type="author"
            name={String(task?.author?.fullName)}
            avatar={task.author?.avatar}
          />

          {assignees.length > 0 && (
            <Employee
              disabled
              type="assignees"
              badge="user-male-female"
              name={i18n.t('tasks.share.modal.employees.assignees', { number: assignees.length })}
            />
          )}

          {currentSharedAccesses
            .filter((i) => !accessesToRemove.includes(i.id))
            .map((access) => (
              <Employee
                accessId={access.id}
                key={access.id}
                type="shared"
                name={access.fullName}
                avatar={access.avatar}
                jobTitle={access.jobTitle}
              />
            ))}

          {!!addedSharedAccesses.length &&
            newAccesses.map((access) => (
              <Employee
                accessId={access.id}
                key={access.id}
                type="shared"
                name={access.fullName}
                avatar={access.avatar ? buildUrl(access.avatar) : undefined}
                jobTitle={access.jobTitle}
              />
            ))}
        </Box>
      )}
    </>
  )
}
